import { DataGrid } from '@core/ui';
import { History, ArrowUpRight, ArrowDownLeft, Lock, Unlock, ShieldCheck } from 'lucide-react';
import React from 'react';
import { isEventLog } from '@core/utils/auditUtils';

/**
 * Movement Audit Table.
 * Visualizes the collection of logs with enriched formatting.
 * Specialized for the "Audit Log" and "Terminal History" views.
 */
const HistoryTable = ({
    data,
    loading,
    hideEvent = true,
    currentPage = 1,
    pageSize = 10,
    // Pagination props forwarded to DataGrid
    hasMore,
    hasPrev,
    totalCount,
    totalPages,
    onNextPage,
    onPrevPage,
    onPageSizeChange,
    onSort,
    sortField,
    sortDir
}) => {
    const size = parseInt(pageSize) || 10;
    const page = parseInt(currentPage) || 1;

    // 1. Dynamic Indicator Color based on Movement Type
    const getIndicatorColor = (log) => {
        if (log.type === 'TERMINAL_LOCK') return 'bg-indigo-500 shadow-sm shadow-indigo-200';
        if (log.type === 'TERMINAL_UNLOCK') return 'bg-orange-500 shadow-sm shadow-orange-200';
        if (isEventLog(log)) return 'bg-blue-400 shadow-sm shadow-blue-200';
        const isEntry = log.type === 'ENTRY' || (log.amount > 0 && !log.type);
        return isEntry ? 'bg-emerald-500 shadow-sm shadow-emerald-200' : 'bg-red-500 shadow-sm shadow-red-200';
    };

    const columns = [
        {
            header: '#',
            width: '52px',
            align: 'center',
            render: (log, index) => (
                <div className="flex justify-center">
                    <span className="font-mono font-black text-[11px] tabular-nums text-slate-400 tracking-widest">
                        {(page - 1) * size + (Number.isInteger(index) ? index : 0) + 1}
                    </span>
                </div>
            )
        },
        {
            header: 'TYPE',
            width: '80px',
            align: 'center',
            sortable: true,
            sortKey: 'type',
            render: (log) => {
                if (log.type === 'TERMINAL_LOCK') {
                    return (
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-indigo-50 text-indigo-600 border border-indigo-100 shadow-sm">
                            <Lock size={20} strokeWidth={2.5} />
                        </div>
                    );
                }
                if (log.type === 'TERMINAL_UNLOCK') {
                    return (
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-orange-50 text-orange-600 border border-orange-100 shadow-sm">
                            <Unlock size={20} strokeWidth={2.5} />
                        </div>
                    );
                }

                if (isEventLog(log)) {
                    return (
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-50 text-blue-500 border border-blue-100 shadow-sm">
                            <ShieldCheck size={20} strokeWidth={2.5} />
                        </div>
                    );
                }

                const isEntry = log.type === 'ENTRY' || (log.amount > 0 && !log.type);
                return (
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${isEntry ? 'bg-emerald-50 text-emerald-600 border border-emerald-100 shadow-sm' : 'bg-red-50 text-red-600 border border-red-100 shadow-sm'}`}>
                        {isEntry ? <ArrowUpRight size={20} strokeWidth={2.5} /> : <ArrowDownLeft size={20} strokeWidth={2.5} />}
                    </div>
                );
            }
        },
        {
            header: 'DATE & TIME',
            width: '140px',
            sortable: true,
            sortKey: 'timestamp',
            render: (log) => (
                <div className="flex flex-col items-start font-black text-primary text-[10px] tracking-widest tabular-nums uppercase whitespace-nowrap">
                    <span>{log.dateLabel?.split(',')[0]}</span>
                    <span className="opacity-40 text-[9px]">{log.dateLabel?.split(',')[1]}</span>
                </div>
            )
        },
        {
            header: 'SKU',
            width: '140px',
            sortable: true,
            sortKey: 'product_sku',
            render: (log) => (
                <span className="font-mono font-black text-amber-500 text-sm tracking-tight uppercase whitespace-nowrap">
                    {log.product_sku || log.product_id || '—'}
                </span>
            )
        },
        {
            header: 'ITEMS',
            render: (log) => {
                const isCatalogEvent = log.product_sku?.startsWith('CATALOG_');
                
                // 1. Identify primary name (Bold Title)
                let mainTitle = log.product_name || '';
                
                // Extract name for structural changes
                if (!mainTitle && isCatalogEvent && log.product_description?.includes(':')) {
                    const parts = log.product_description.split(':');
                    if (parts.length > 1) mainTitle = parts[1].trim();
                }

                // Use description for product movements if name is missing
                if (!mainTitle && !isCatalogEvent) {
                    mainTitle = log.product_description || 'General Item';
                }

                // Final Fallback for title
                if (!mainTitle) mainTitle = isCatalogEvent ? 'System Catalog' : (log.product_category || 'General');
                
                // 2. Identify subtitle (The "Noticeable" part)
                // Use the 'note' field as the secondary info, or fallback to Category
                const subTitle = log.note || log.product_category || 'General';

                // 3. Product's actual description
                const productDesc = log.product_description || '';
                // Only show productDesc if we have both product_name and a different product_description
                const showProductDesc = log.product_name && productDesc && productDesc !== log.product_name && !isCatalogEvent;

                return (
                    <div className="flex flex-col gap-0.5 py-1">
                        <span className="font-extrabold text-[11px] text-slate-900 uppercase tracking-wider leading-tight line-clamp-1">
                            {mainTitle}
                        </span>
                        {showProductDesc && (
                            <span className="text-[10px] text-slate-400 italic font-medium tracking-tight max-w-[300px] truncate block leading-tight">
                                {productDesc}
                            </span>
                        )}
                        {subTitle && subTitle !== productDesc && (
                            <span className="text-[10px] text-slate-400 italic font-medium tracking-tight max-w-[300px] truncate block leading-tight opacity-75">
                                {subTitle}
                            </span>
                        )}
                    </div>
                );
            }
        },
        {
            header: 'MEMBER',
            sortable: true,
            sortKey: 'user',
            render: (log) => {
                const operatorName = log.member_name || log.mechanic_name || log.user_name || log.user || 'System';
                return (
                    <div className="flex flex-col py-1">
                        <span className="text-[11px] font-bold text-slate-700 uppercase leading-tight">
                            {operatorName}
                        </span>
                    </div>
                );
            }
        },
        {
            header: 'QTY',
            width: '100px',
            align: 'center',
            sortable: true,
            sortKey: 'amount',
            render: (log) => {
                // CREATE: show initial quantity in blue (it's an event but has a meaningful qty)
                if (log.type === 'CREATE') {
                    const qty = Math.abs(Number(log.amount) || 0);
                    return (
                        <div className="flex justify-center">
                            <span className="font-mono font-black text-lg tabular-nums px-3 py-1 rounded-lg text-blue-600 bg-blue-50">
                                {qty}
                            </span>
                        </div>
                    );
                }

                if (isEventLog(log)) {
                    // Translate long type names to fit nicely in the pill
                    let eventLabel = log.type || 'EVENT';
                    if (eventLabel === 'STRUCTURAL_CHANGE') {
                        // Better identify if it was an activation or deactivation based on the note
                        if (log.note?.toLowerCase().includes('deactivated')) eventLabel = 'DEACTIVATE';
                        else if (log.note?.toLowerCase().includes('activated')) eventLabel = 'ACTIVATE';
                        else if (log.note?.toLowerCase().includes('deleted') || log.note?.toLowerCase().includes('removed')) eventLabel = 'DELETE';
                        else eventLabel = 'CONFIG';
                    }
                    if (eventLabel === 'METADATA_UPDATE') eventLabel = 'UPDATE';
                    if (eventLabel === 'TERMINAL_LOCK') eventLabel = 'LOCK';
                    if (eventLabel === 'TERMINAL_UNLOCK') eventLabel = 'UNLOCK';
                    if (eventLabel === 'BULK_ARCHIVE') eventLabel = 'B.ARCHIVE';

                    return (
                        <div className="flex justify-center">
                            <span className="font-mono font-black text-[10px] tabular-nums px-3 py-1 rounded-lg bg-blue-50 text-blue-600 uppercase tracking-widest">
                                {eventLabel}
                            </span>
                        </div>
                    );
                }

                return (
                    <div className="flex justify-center">
                        <span className={`font-mono font-black text-lg tabular-nums px-3 py-1 rounded-lg ${log.type === 'ENTRY' ? 'text-emerald-600 bg-emerald-50' : 'text-red-600 bg-red-50'}`}>
                            {Number.isNaN(Number(log.amount)) ? '—' : Math.abs(Number(log.amount))}
                        </span>
                    </div>
                );
            }
        }
    ];

    return (
        <div className="flex-1 flex flex-col gap-4 min-h-0 min-w-0 w-full">
            <DataGrid
                loading={loading}
                data={data}
                emptyTitle="No movements"
                emptyDescription="No inventory movements have been registered yet."
                emptyIcon={History}
                columns={columns}
                getIndicatorColor={getIndicatorColor}
                disableInfiniteScroll={true}
                // Sorting
                onSort={onSort}
                sortField={sortField}
                sortDir={sortDir}
                // Pagination
                hasMore={hasMore}
                hasPrev={hasPrev}
                totalCount={totalCount}
                totalPages={totalPages}
                currentPage={page}
                pageSize={size}
                onNextPage={onNextPage}
                onPrevPage={onPrevPage}
                onPageSizeChange={onPageSizeChange}
            />
        </div>
    );
};

export default React.memo(HistoryTable);
